<template>
  <v-dialog v-model="dialog" persistent max-width="1000px">
    <template v-slot:activator="{ on }">
      <v-btn v-if="isEditButton" icon v-on="on">
        <v-icon>fa-edit</v-icon>
      </v-btn>
      <v-btn v-else color="info" dark v-on="on">
        <v-icon class="pr-1">fa-plus-circle</v-icon>
          Add New
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <span class="text-h5">Core Statement Code Mapping Rule</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row >
            <v-col cols="12">
              <v-autocomplete label="Kasasa Statement Code"
                :items="kasasaStatementCodes"
                item-text="text"
                item-value="key"
                v-model="workingRule.kasasaStatementCode"
                @change="onModification"
                :loading="loading"/>
            </v-col>

            <v-col cols="12">
              <v-combobox label = "Core Statement Code"
                id="coreCodes"
                multiple
                return-object
                v-model="workingRule.coreStatementCodes"
                append-icon
                chips
                deletable-chips
                :rules="requiredRule"
                :search-input.sync="enteredCoreCode"
                @update:search-input="splitIntoStatementCodes"
                @change="onModification">
              </v-combobox>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn id="cancelButton" large @click="cancel" :disabled="saving">Cancel</v-btn>
          <v-btn id="saveButton" large class="blue white--text" @click="save"
            :loading="saving" :disabled="!isModificationSavable">
              Save
          </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState } from 'vuex';
import {
  Component, Prop, Watch, Inject,
} from 'vue-property-decorator';
import { StatmentCodeMappingRuleResource, StatmentCodeMappingRule }
  from '@/api/statementcodes/';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';

@Component({ computed: mapState(['fiId']) })
export default class StatmentCodeMappingRuleDialog extends Vue {
  @Inject('StatmentCodeMappingRuleResource')
  ruleResource!:StatmentCodeMappingRuleResource;

  @Prop()
  rule!:StatmentCodeMappingRule;

  @Prop({ default: [] })
  existingRules!:Array<StatmentCodeMappingRule>;

  @Prop()
  isEditButton!:boolean;

  @Prop()
  statmentCodeMap!:Map<string, string>;

  workingRule:StatmentCodeMappingRule =
    StatmentCodeMappingRuleDialog.createEmptyRule();

  fiId!:string;

  dialog:boolean = false;

  saving:boolean = false;

  enteredCoreCode:string = '';

  kasasaStatementCodes:{ [key: string]: string }[] = [];

  isModificationSavable:boolean = false;

  loading = false;

  delimiter: string = ',';

  requiredRule:Array<any> = [(value: any) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return 'Required';
    }
    return true;
  }];

  static createEmptyRule() : StatmentCodeMappingRule {
    return {
      id: undefined,
      coreStatementCodes: [],
      kasasaStatementCode: '',
    };
  }

  @Watch('dialog')
  onDialogToggle() {
    if (this.dialog) {
      this.saving = false;
      this.loading = true;
      this.initializeWorkingRule();
      if (this.kasasaStatementCodes.length === 0
        && this.statmentCodeMap !== undefined) {
        this.buildKasasaStatementCodesFromMap();
      }
      this.loading = false;
    }
  }

  initializeWorkingRule() {
    this.isModificationSavable = false;
    if (this.rule) {
      Object.assign(this.workingRule, this.rule);
    } else {
      this.workingRule = StatmentCodeMappingRuleDialog.createEmptyRule();
    }
  }

  buildKasasaStatementCodesFromMap() {
    this.statmentCodeMap.forEach((value: string, key: string) => {
      const text:string = value;
      this.kasasaStatementCodes.push({
        key,
        text,
      });
    });
  }

  cancel() {
    this.initializeWorkingRule();
    this.dialog = false;
  }

  save() {
    // Force combos to update because they might still have focus when user clicks save.
    if (this.enteredCoreCode && this.enteredCoreCode.trim()
      && !this.workingRule.coreStatementCodes.includes(this.enteredCoreCode.trim())) {
      this.workingRule.coreStatementCodes.push(this.enteredCoreCode.trim());
    }

    this.saving = true;
    if (!this.areWorkingCoresUnique()) {
      this.saving = false;
      this.setError('Duplicate Core Statement Code!');
      return;
    }
    this.ruleResource.save(this.fiId, this.workingRule)
      .then((savedRule:StatmentCodeMappingRule) => {
        if (this.rule) {
          Object.assign(this.rule, savedRule);
        }
        this.$emit('success', savedRule);
        this.dialog = false;
        this.initializeWorkingRule();
      })
      .catch((error:any) => {
        this.saving = false;
        this.showError();
      });
  }

  areWorkingCoresUnique(): boolean {
    const allCores = new Set<string>();
    this.existingRules.forEach((rule) => {
      if (rule.id !== this.workingRule.id) {
        rule.coreStatementCodes.forEach((coreType) => {
          allCores.add(coreType);
        });
      }
    });
    let isUnique = true;
    this.workingRule.coreStatementCodes.forEach((coreType) => {
      if (allCores.has(coreType)) {
        isUnique = false;
      }
    });
    return isUnique;
  }

  splitIntoStatementCodes() {
    if (this.enteredCoreCode && this.enteredCoreCode.split(this.delimiter).length > 1) {
      this.workingRule.coreStatementCodes.push(...this.enteredCoreCode
        .split(this.delimiter).filter(term => !this.workingRule.coreStatementCodes.includes(term) && term.trim() !== ''));
      this.enteredCoreCode = '';
      this.onModification();
    }
  }

  onModification() {
    const oldCores = new Set<string>();
    if (this.rule) {
      this.rule.coreStatementCodes.forEach((coreType) => {
        oldCores.add(coreType);
      });
    }
    const newCores = new Set<string>();
    if (this.workingRule && this.workingRule.coreStatementCodes) {
      this.workingRule.coreStatementCodes.forEach((coreType) => {
        newCores.add(coreType);
      });
    }
    const deletedCores = new Set([...oldCores].filter(x => !newCores.has(x)));
    const addedCores = new Set([...newCores].filter(x => !oldCores.has(x)));

    this.isModificationSavable = this.workingRule.kasasaStatementCode !== '' // kasasa code selected
      && this.workingRule.coreStatementCodes.length > 0 // non empty core code
      && (deletedCores.size > 0 // core code removed
          || addedCores.size > 0 // core code added
          || (this.rule
              && this.rule.kasasaStatementCode // kasasa code modified
              !== this.workingRule.kasasaStatementCode));
  }

  showError() {
    const al = new Alert('An unexpected error occurred.', NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  setError(message:string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
</script>

<style lang="scss" scoped>
</style>
